import api from '@/api/client/axios';
import { ILoginForm, Organization, User } from '@/types';
import { useQueryClient } from '@tanstack/react-query';
import {
  PropsWithChildren,
  useEffect,
  useState,
  createContext,
  useContext,
} from 'react';

declare module 'axios' {
  interface AxiosRequestConfig {
    _retry?: boolean;
  }
}

interface AuthContextType {
  user: User | null;
  organization: Organization | null;
  isAuthenticated: boolean;
  authLoading: boolean;
  handleLogin: (data: ILoginForm) => Promise<any>;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

export default function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [authLoading, setAuthLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  // Fetch user on initial load - cookies are automatically sent
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await api.get('/auth/me');
        setUser(res.data.user);
        setOrganization(res.data.organization);
        setIsAuthenticated(true);
      } catch (err) {
        setUser(null);
        setOrganization(null);
        setIsAuthenticated(false);
      } finally {
        setAuthLoading(false);
      }
    };

    fetchUser();
  }, []);

  const handleLogin = async (data: ILoginForm) => {
    try {
      const response = await api.post('/auth/login', data);
      const { user, organization } = response.data;

      setUser(user);
      setOrganization(organization);
      setIsAuthenticated(true);
      return { user, organization };
    } catch (err) {
      console.error('Login failed:', err);
      setUser(null);
      setOrganization(null);
      setIsAuthenticated(false);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout'); // Changed to POST to clear cookies properly
      setUser(null);
      setOrganization(null);
      setIsAuthenticated(false);
      queryClient.clear();
    } catch (err) {
      // Even if logout request fails, clear local state
      setUser(null);
      setOrganization(null);
      setIsAuthenticated(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        organization,
        isAuthenticated,
        authLoading,
        handleLogin,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error('useAuth must be used within a AuthProvider.');
  }
  return ctx;
};
